import { FunctionalComponent } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { Search, Link as LinkIcon, Loader2, Building2, ExternalLink } from 'lucide-preact'
import { companiesApi, type CompanySearchResultDto, type CompanyEnrichedDto } from '@/api/companies'
import { handleApiError } from '@/utils/errorHandling'
import { useToast } from '@/hooks/useToast'
import { useDebouncedValue } from '@/hooks'

interface CompanyLookupProps {
  companyId: string
  companyName?: string | null
  onChange: (companyId: string, companyName: string) => void
  onLoadingChange?: (loading: boolean) => void
  error?: string
  disabled?: boolean
  required?: boolean
}

export const CompanyLookup: FunctionalComponent<CompanyLookupProps> = ({
  companyId,
  companyName,
  onChange,
  onLoadingChange,
  error,
  disabled = false,
  required = false
}) => {
  const { showError } = useToast()
  const [companyQuery, setCompanyQuery] = useState('')
  const [linkedInInput, setLinkedInInput] = useState('')
  const debouncedCompanyQuery = useDebouncedValue(companyQuery, 300)
  const [companyResults, setCompanyResults] = useState<CompanySearchResultDto[]>([])
  const [companyLoading, setCompanyLoading] = useState(false)
  const [enrichLoading, setEnrichLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const companySearchContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (debouncedCompanyQuery.length < 3) { setCompanyResults([]); return }
    if (abortRef.current) { abortRef.current.abort() }
    const controller = new AbortController()
    abortRef.current = controller
    setCompanyLoading(true)
    companiesApi.search(debouncedCompanyQuery, 25, controller.signal)
      .then(resp => {
        if (!controller.signal.aborted) {
          setCompanyResults(resp.data || [])
        }
      })
      .catch(err => {
        if (err?.name !== 'AbortError') {
          console.error('Company search error', err)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setCompanyLoading(false)
      })
    return () => {
      controller.abort()
    }
  }, [debouncedCompanyQuery])

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!companySearchContainerRef.current) return
      if (!companySearchContainerRef.current.contains(e.target as Node)) {
        setCompanyResults([])
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  async function enrichById() {
    if (!linkedInInput) return

    // Validate input
    const trimmed = linkedInInput.trim()
    if (trimmed.length > 2083) {
      showError('Validation Error', 'Input exceeds maximum length of 2083 characters.')
      return
    }

    if (trimmed.includes('/') || trimmed.startsWith('http')) {
      // Validate it's a LinkedIn URL
      if (!trimmed.includes('linkedin.com/company/')) {
        showError('Invalid URL', 'Must be a LinkedIn company URL.')
        return
      }
    } else if (!/^\d+$/.test(trimmed)) {
      // If not a URL, must be all digits
      showError('Invalid Input', 'Must be a LinkedIn company ID (number) or LinkedIn company URL.')
      return
    }

    setEnrichLoading(true)
    onLoadingChange?.(true)
    try {
      const resp = await companiesApi.enrichByLinkedInId(linkedInInput)
      if (resp.error) {
        handleApiError(resp, showError, () => { }, 'Fetch Failed')
        return
      }
      const company = resp.data as CompanyEnrichedDto | undefined
      if (company) {
        const newName = company.Name || company.NormalizedName || companyName || ''
        // Use the LinkedIn ID from the response, fallback to Id if LinkedInId is not available
        const linkedInId = company.LinkedInId || company.Id || trimmed
        onChange(linkedInId, newName)
      }
    } catch (e) {
      handleApiError(e, showError, () => { }, 'Fetch Failed')
    } finally {
      setEnrichLoading(false)
      onLoadingChange?.(false)
    }
  }

  function handleCompanySelect(result: CompanySearchResultDto) {
    const id = (result.Id || '').toString().trim()
    const name = result.Name?.trim() || result.NormalizedName?.trim() || id
    onChange(id, name)
    setCompanyQuery('')
    setCompanyResults([])
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
        💡 Search companies in your database first. If not found, enter the LinkedIn Company ID below to fetch fresh data.
      </div>

      <div className="flex flex-col gap-1" ref={companySearchContainerRef}>
        <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
          Search Your Companies
        </label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={companyQuery}
            onInput={e => setCompanyQuery((e.target as HTMLInputElement).value)}
            placeholder="Search companies in your database (min 3 chars)"
            disabled={disabled}
            className="w-full pl-8 pr-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />
        </div>
        {companyLoading && (
          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Searching...
          </div>
        )}
        {!companyLoading && companyResults.length > 0 && (
          <ul className="max-h-44 overflow-auto rounded-md divide-y bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 divide-slate-200 dark:divide-slate-600">
            {companyResults.map(r => {
              const displayId = r.Id
              return (
                <li
                  key={r.Id}
                  className="p-2 cursor-pointer text-xs flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700"
                  onClick={() => handleCompanySelect(r)}
                >
                  {r.Logo && <img src={r.Logo} alt="logo" className="w-5 h-5 rounded object-cover" />}
                  <span className="font-medium truncate text-slate-700 dark:text-slate-200">
                    {r.Name || r.NormalizedName || displayId || 'Company'}
                  </span>
                  {displayId && (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {displayId}
                    </span>
                  )}
                  {r.StaffCountRange && (
                    <span className="text-[10px] ml-auto text-slate-500 dark:text-slate-400">
                      {r.StaffCountRange}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 items-start">
        <div className="col-span-2 flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
            Or Enter LinkedIn Company ID or URL {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={linkedInInput}
            maxLength={2083}
            onInput={e => setLinkedInInput((e.target as HTMLInputElement).value)}
            placeholder="e.g. 123456 or https://linkedin.com/company/orbital-x"
            disabled={disabled}
            className={`px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 ${
              error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'
            } bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-50`}
          />
          {error && (
            <span className="text-xs text-red-600 dark:text-red-400">
              {error}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700 dark:text-slate-300 opacity-0">Action</label>
          <button
            type="button"
            disabled={disabled || !linkedInInput || enrichLoading}
            onClick={enrichById}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            title="Fetch company data from LinkedIn"
          >
            {enrichLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LinkIcon className="w-3.5 h-3.5" />}
            Fetch from LinkedIn
          </button>
        </div>
      </div>

      {companyName && (
        <div className="flex items-start justify-between gap-4 p-3 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">COMPANY</span>
          </div>
          <div className="text-right flex-1 min-w-0">
            {companyId ? (
              <a
                href={`https://www.linkedin.com/company/${companyId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:underline text-sm truncate max-w-[320px] text-primary-600 dark:text-primary-400"
                onClick={e => e.stopPropagation()}
              >
                {companyName}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : (
              <span className="text-sm truncate max-w-[320px] text-slate-700 dark:text-slate-200" title={companyName}>
                {companyName}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CompanyLookup
